import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@nt/ui/card";
import { cn } from "@nt/ui/cn";
import type { ReactNode } from "react";

/**
 * One white card on the page with a header row (title, description, actions)
 * and its body. Pages compose sections side by side or stacked; a section
 * never holds another section or card — group inside one with rules,
 * spacing or a `bg-muted` block.
 */
export function Section({
  title,
  description,
  actions,
  children,
  id,
  className,
  contentClassName,
}: {
  title: ReactNode;
  description?: ReactNode;
  /** Buttons or links at the end of the header row (under it when narrow). */
  actions?: ReactNode;
  children?: ReactNode;
  id?: string;
  className?: string;
  /** E.g. `px-0` for a table that runs edge to edge. */
  contentClassName?: string;
}) {
  const headingId = id ? `${id}-title` : undefined;
  return (
    <Card
      data-section=""
      id={id}
      aria-labelledby={headingId}
      className={className}
    >
      <CardHeader>
        <CardTitle id={headingId} role="heading" aria-level={2}>
          {title}
        </CardTitle>
        {description ? <CardDescription>{description}</CardDescription> : null}
        {actions ? (
          <CardAction className="flex flex-wrap items-center gap-2">
            {actions}
          </CardAction>
        ) : null}
      </CardHeader>
      {children !== undefined && children !== null ? (
        <CardContent className={cn(contentClassName)}>{children}</CardContent>
      ) : null}
    </Card>
  );
}
